import React, { createContext, useContext, useState } from 'react';

const TranscriptionContext = createContext();

export const useTranscription = () => useContext(TranscriptionContext);

export const TranscriptionProvider = ({ children }) => {
  const [connected] = useState(true); // Always connected with HTTP
  const [activeSession, setActiveSession] = useState(null);

  // Get the backend URL from environment or default to localhost:8001
  const API_URL = process.env.REACT_APP_API_URL || 'https://deva-survey-api.promptcatalog.online';

  // Function to join a specific session
  const joinSession = async (sessionId) => {
    try {
      console.log(`🔗 Attempting to join session ${sessionId}`);
      const response = await fetch(`${API_URL}/sessions/${sessionId}/transcription/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'ok') {
        setActiveSession(sessionId);
        console.log(`✅ Successfully joined session ${sessionId}`);
        return data;
      } else {
        const errorMessage = data.message || 'Unknown error';
        console.error('❌ Failed to join session:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error joining session:', error.message);
      throw error;
    }
  };

  // Function to leave the current session
  const leaveSession = () => {
    if (activeSession) {
      console.log(`✅ Left session ${activeSession}`);
      setActiveSession(null);
    }
  };

  // Function to start transcription
  const startTranscription = async (sessionId) => {
    try {
      console.log(`🎤 Starting transcription for session ${sessionId}`);
      const response = await fetch(`${API_URL}/sessions/${sessionId}/transcription/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'ok') {
        console.log(`🎤 ✅ Transcription started successfully`);
        return data;
      } else {
        const errorMessage = data.message || 'Failed to start transcription';
        console.error('🎤 ❌ Failed to start transcription:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('🎤 ❌ Error starting transcription:', error.message);
      throw error;
    }
  };

  // Function to get transcription status
  const getTranscriptionStatus = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/sessions/${sessionId}/transcription/status`);
      const data = await response.json();
      
      if (response.ok && data.status === 'ok') {
        return data;
      } else {
        throw new Error(data.message || 'Failed to get transcription status');
      }
    } catch (error) {
      console.error('❌ Error getting transcription status:', error.message);
      throw error;
    }
  };

  // Function to send audio data for transcription (using attachments API)
  const sendAudioChunk = async (audioData, sessionId, fileExtension = 'webm') => {
    try {
      console.log('🎵 Preparing audio data for upload...', typeof audioData, audioData);
      
      // Determine MIME type based on extension
      let mimeType = 'audio/webm'; // default
      if (fileExtension === 'webm') {
        mimeType = 'audio/webm';
      } else if (fileExtension === 'ogg') {
        mimeType = 'audio/ogg';
      } else if (fileExtension === 'wav') {
        mimeType = 'audio/wav';
      } else if (fileExtension === 'mp4' || fileExtension === 'm4a') {
        mimeType = 'audio/mp4';
      }
      
      // Convert various audio data formats to Blob
      let audioBlob;
      if (audioData instanceof Blob) {
        audioBlob = audioData;
      } else if (audioData instanceof ArrayBuffer) {
        audioBlob = new Blob([audioData], { type: mimeType });
      } else if (Array.isArray(audioData)) {
        // Assume it's an array of audio chunks (Uint8Array or similar)
        audioBlob = new Blob(audioData, { type: mimeType });
      } else {
        throw new Error('Invalid audio data format. Expected Blob, ArrayBuffer, or Array.');
      }

      const formData = new FormData();
      formData.append('file', audioBlob, `recording.${fileExtension}`);

      console.log('🎵 Sending audio blob of size:', audioBlob.size, 'bytes');

      const response = await fetch(`${API_URL}/sessions/${sessionId}/attachments`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (response.ok) {
        console.log('🎵 ✅ Audio chunk sent successfully:', data);
        return data;
      } else {
        throw new Error(data.error || 'Failed to send audio chunk');
      }
    } catch (error) {
      console.error('🎵 ❌ Error sending audio chunk:', error.message);
      throw error;
    }
  };

  // Function to send WebRTC audio data
  const sendWebRTCAudio = async (audioBlob, sessionId, fileExtension = 'webm') => {
    return sendAudioChunk(audioBlob, sessionId, fileExtension);
  };

  // Function to process a complete transcription
  const processTranscription = async (transcription, sessionId) => {
    try {
      console.log(`📝 Processing transcription for session ${sessionId}`);
      // This could be sent to an AI processing endpoint if needed
      console.log('📝 Transcription:', transcription);
      return { status: 'ok', message: 'Transcription processed' };
    } catch (error) {
      console.error('📝 ❌ Error processing transcription:', error.message);
      throw error;
    }
  };

  // Stub functions for event listeners (HTTP doesn't have real-time events)
  const onTranscription = (callback) => {
    // In HTTP mode, we could implement polling here if needed
    return () => {};
  };

  const onAnswersFilled = (callback) => {
    return () => {};
  };

  const onRealtimeResult = (callback) => {
    return () => {};
  };

  const onProcessingStatus = (callback) => {
    return () => {};
  };

  const onError = (callback) => {
    return () => {};
  };

  const value = {
    socket: null, // No socket in HTTP mode
    connected,
    activeSession,
    joinSession,
    leaveSession,
    startTranscription,
    sendAudioChunk,
    sendWebRTCAudio,
    processTranscription,
    getTranscriptionStatus,
    onTranscription,
    onAnswersFilled,
    onRealtimeResult,
    onProcessingStatus,
    onError
  };

  return (
    <TranscriptionContext.Provider value={value}>
      {children}
    </TranscriptionContext.Provider>
  );
};

export default TranscriptionContext;
